/**
 * Copyright (c) 2026 上海安能聚创供应链管理有限公司.
 * All rights reserved.
 * 公司内部代码，未经书面授权，不得以任何形式复制、传播或用于与本项目无关的其他用途.
 */
import { find, get, isArray } from 'lodash';

import BigNumber from 'bignumber.js';
import cloneDeep from 'lodash/cloneDeep';
import { crudRequest } from '@/services/api';
import { getToDay } from '@/utils/date';
import keyBy from 'lodash/keyBy';
import { message as messagePop } from 'antd';
import moment from 'moment';
import sortBy from 'lodash/sortBy';

const isDev = process.env.NODE_ENV !== 'production';
const [startDate, endDate] = getToDay();

// 根据 smallArray 的 sectionOrder 对 largeArray 进行排序
function sortLargeArrayBySmallArray(largeArray, smallArray) {
  // // 输入参数验证
  // if (!Array.isArray(largeArray) || !Array.isArray(smallArray)) {
  //   console.warn('sortLargeArrayBySmallArray: 输入参数必须是数组');
  //   return [];
  // }

  // // 如果原始数组为空，直接返回
  // if (largeArray.length === 0) {
  //   console.log('largeArray 为空，直接返回空数组');
  //   return [];
  // }

  // 过滤掉这两个对象里面不存在departureSiteCode 和  arriveSiteCode 的
  const filteredLargeArray = largeArray.filter(
    (item) => item && item.departureSiteCode && item.arrivalSiteCode,
  );
  const filteredSmallArray = smallArray.filter(
    (item) => item && item.departureSiteCode && item.arriveSiteCode,
  );

  console.log('filteredLargeArray', filteredLargeArray);
  console.log('filteredSmallArray', filteredSmallArray);

  // 如果 filteredLargeArray 为空，直接返回空数组，避免不必要的处理
  if (filteredLargeArray.length === 0) {
    console.log('filteredLargeArray 为空，直接返回空数组');
    return [];
  }

  // 创建一个映射，生成一个用于排序的键
  const sortOrderMap = keyBy(
    filteredSmallArray,
    (item) => `${item.departureSiteCode}-${item.arriveSiteCode}`,
  );

  // 添加排序信息到每个记录
  const dataWithSortInfo = filteredLargeArray.map((item) => {
    const key = `${item.departureSiteCode}-${item.arrivalSiteCode}`;
    return {
      ...item,
      _hasOrderRule: !!sortOrderMap[key],
      _sectionOrder: sortOrderMap[key] ? sortOrderMap[key].sectionOrder : Infinity,
      _departureSiteCode: item.departureSiteCode,
      _arrivalSiteCode: item.arrivalSiteCode,
    };
  });

  // 按出发站点(departureSiteCode)分组
  const groupedByDepartureSite = {};
  dataWithSortInfo.forEach((item) => {
    const key = item._departureSiteCode;
    if (!groupedByDepartureSite[key]) {
      groupedByDepartureSite[key] = [];
    }
    groupedByDepartureSite[key].push(item);
  });

  // 对每组内部数据排序
  // 首先按照是否有排序规则排序，有规则的排前面
  // 然后按照排序规则值排序
  // 最后按照到达站点代码排序
  Object.keys(groupedByDepartureSite).forEach((key) => {
    groupedByDepartureSite[key].sort((a, b) => {
      // 优先按是否有排序规则排序
      if (a._hasOrderRule !== b._hasOrderRule) {
        return a._hasOrderRule ? -1 : 1;
      }

      // 然后按照规则值排序
      if (a._sectionOrder !== b._sectionOrder) {
        return a._sectionOrder - b._sectionOrder;
      }

      // 最后按照到达站点代码排序
      return a._arrivalSiteCode.localeCompare(b._arrivalSiteCode);
    });
  });

  // 对departureSiteCode进行排序
  // 优先考虑是否有任何带排序规则的记录
  const sortedDepartureSiteCodes = Object.keys(groupedByDepartureSite).sort((a, b) => {
    const hasRuleA = groupedByDepartureSite[a].some((item) => item._hasOrderRule);
    const hasRuleB = groupedByDepartureSite[b].some((item) => item._hasOrderRule);

    if (hasRuleA !== hasRuleB) {
      return hasRuleA ? -1 : 1;
    }

    // 如果两个出发站点都有排序规则的记录，按最小排序规则值排序
    if (hasRuleA && hasRuleB) {
      const minOrderA = Math.min(...groupedByDepartureSite[a].map((item) => item._sectionOrder));
      const minOrderB = Math.min(...groupedByDepartureSite[b].map((item) => item._sectionOrder));
      if (minOrderA !== minOrderB) {
        return minOrderA - minOrderB;
      }
    }

    // 否则按出发站点代码排序
    return a.localeCompare(b);
  });

  // 按照排序后的departureSiteCode顺序平铺数组
  const sortedData = [];
  sortedDepartureSiteCodes.forEach((siteCode) => {
    sortedData.push(...groupedByDepartureSite[siteCode]);
  });

  // 移除我们添加的排序字段
  const cleanedData = sortedData.map((item) => {
    const { _hasOrderRule, _sectionOrder, _departureSiteCode, _arrivalSiteCode, ...rest } = item;
    return rest;
  });

  console.log(
    `找到匹配排序规则的记录: ${sortedData.filter((item) => item._hasOrderRule).length}/${
      filteredLargeArray.length
    }`,
  );

  const obj = {
    'sortLargeArrayBySmallArray largeArray slice 0-3': largeArray.slice(0, 3),
    'sortLargeArrayBySmallArray smallArray slice 0-3': smallArray.slice(0, 3),
    'sortLargeArrayBySmallArray sortOrderMap keys': Object.keys(sortOrderMap).slice(0, 3),
    'sortLargeArrayBySmallArray sortOrderMap preview': Object.entries(sortOrderMap).slice(0, 3),
    'sortLargeArrayBySmallArray filteredLargeArray slice 0-3': filteredLargeArray.slice(0, 3),
    'sortLargeArrayBySmallArray data slice 0-3': cleanedData.slice(0, 3),
    'sortLargeArrayBySmallArray hasMatchingKeys slice 0-3': sortedData
      .filter((item) => item._hasOrderRule)
      .slice(0, 3)
      .map((item) => {
        const { _hasOrderRule, _sectionOrder, _departureSiteCode, _arrivalSiteCode, ...rest } =
          item;
        return rest;
      }),
    'sortLargeArrayBySmallArray data map 0-3': cleanedData
      .map(
        (item) =>
          `${item.departureSiteCode}-${item.arrivalSiteCode}-${item.departureSite}-${item.arrivalSite}`,
      )
      .slice(0, 3),
  };
  console.log(obj);

  return cleanedData;
}
/**
 * 根据发送站点编码和到达站点编码获取载货率标准
 *
 * @param data 数据源
 * @param sendSiteCode 发送站点编码
 * @param arriveSiteCode 到达站点编码
 * @returns 转换后的载货率标准百分数
 */
function getLoadRateStandard(data, sendSiteCode, arriveSiteCode) {
  // 找到所有匹配的记录
  const matchingRecords = data.filter(
    (item) => item.sendSiteCode === sendSiteCode && item.arriveSiteCode === arriveSiteCode,
  );

  // 如果找到多个匹配的记录，优先选择carType为0的记录
  const selectedRecord = matchingRecords.find((item) => item.carType === '0') || matchingRecords[0];

  // 如果找到了匹配的记录，返回转换后的百分数
  if (selectedRecord) {
    // 使用 Number 处理浮点数，并转换为整数百分比
    const fullRateStandard = Number(selectedRecord.fullRateStandard || 1);
    // 将小数转为百分比，并四舍五入为整数
    return `${Math.round(fullRateStandard * 100)}%`;
  }

  // 如果没有找到匹配的记录，返回默认值 100%
  return '100%';
}

/**
 * 获取里程
 *
 * @param data 里程数组
 * @param departureSiteCode 出发站点编码
 * @param arriveSiteCode 到达站点编码
 * @returns 返回距离值，若未找到则返回空字符串
 */
function getDistance(data, departureSiteCode, arriveSiteCode) {
  const distanceData =
    find(
      data,
      (item) =>
        item.departureSiteCode === departureSiteCode &&
        item.arriveSiteCode === arriveSiteCode &&
        item.carType === '0',
    ) ||
    find(
      data,
      (item) =>
        item.departureSiteCode === departureSiteCode && item.arriveSiteCode === arriveSiteCode,
    );

  return get(distanceData, 'distance', '');
}

const paramsForm = {
  dateRange: [startDate.clone(), endDate.clone()],
  carDateRange: [startDate.clone(), endDate.clone()],
  crrentDateRange: '',
  centerFilter: '',
  access: '',
  collcetSearchParams: {
    departProvinceArea: '',
    arrivalProvinceArea: '',
    startDate: startDate.clone().format('YYYY-MM-DD HH:mm'),
    endDate: endDate.clone().format('YYYY-MM-DD HH:mm'),
  },

  detailSearchParams: {
    // 区域属性
    areaPropertys: ['10', '20', '30', '40'],
    departProvinceArea: '',
    arrivalProvinceArea: '',
    departCenter: '',
    arrivalCenter: '',
    startDate: startDate.clone().format('YYYY-MM-DD HH:mm'),
    endDate: endDate.clone().format('YYYY-MM-DD HH:mm'),
  },
  centerCode: '',
};
const handleParme = (parme, key) => {
  console.log('parme handleParme', parme);
  if (parme) {
    if (isArray(parme)) {
      let arr = parme?.map((item) => item[key]).filter((item) => item);
      return arr?.length ? arr : undefined;
    } else {
      return parme[key] && parme['siteTypeCode'] !== '区域' ? [parme[key]] : undefined;
    }
  }
  return undefined;
};

const handleParme2 = (parme, key) => {
  console.log('parme handleParme2', parme);
  if (parme) {
    if (isArray(parme)) {
      let arr = parme?.map((item) => item[key]).filter((item) => item);
      return arr?.length ? arr : undefined;
    } else {
      return parme[key] && parme['siteTypeCode'] === '区域' ? [parme[key]] : undefined;
    }
  }
  return undefined;
};

/**
 * 获取满载率标准
 *
 * @param params 请求参数
 * @param params.destinationProvinceAreaCodes 目的营运区编码数组
 * @param params.nextSiteCodes 目的站点数组
 * @param params.originProvinceAreaCodes 始发营运区编码数组
 * @param params.scanSiteCodes 始发站点数组
 * @returns 完整的满载率标准数组
 */
async function fetchFullRateStandard(params) {
  console.log('%c [ params ]: ', 'color: #bf2c9f; background: pink; font-size: 13px;', params);
  try {
    const response = await crudRequest('/api/route/selectFullRate', {
      arriveProvinceCode: params?.destinationProvinceAreaCodes?.[0], // 目的营运区编码
      arriveSiteCode: params?.nextSiteCodes?.[0], // 目的站点
      sendProvinceCode: params?.originProvinceAreaCodes?.[0], // 始发营运区编码
      sendSiteCode: params?.scanSiteCodes?.[0], // 始发站点
      originRegionCode: params?.originRegionCodes?.[0], // 始发区域编码
      destinationRegionCode: params?.destinationRegionCodes?.[0], // 目的区域编码
    });

    const { data: { rows: fullRateStandard = [] } = {} } = response || {};
    return fullRateStandard;
  } catch (error) {
    console.error('Error fetching fullRateStandard:', error);
    return [];
  }
}

/**
 * 获取流向排序
 *
 * @param params 请求参数
 * @param params.destinationProvinceAreaCodes 目的营运区编码数组
 * @param params.nextSiteCodes 目的站点数组
 * @param params.originProvinceAreaCodes 始发营运区编码数组
 * @param params.scanSiteCodes 始发站点数组
 * @returns 返回费率标准数组
 */
async function fetchFlowOrder(params) {
  console.log('%c [ params ]: ', 'color: #bf2c9f; background: pink; font-size: 13px;', params);
  try {
    const response = await crudRequest('/api/route/getRoadNumDisposition', {
      arriveSiteCode: params?.nextSiteCodes?.[0], // 目的营运区编码
      arrivalProvinceAreaCode: params?.destinationProvinceAreaCodes?.[0], // 目的站点
      departureProvinceAreaCode: params?.originProvinceAreaCodes?.[0], // 始发营运区编码
      departureSiteCode: params?.scanSiteCodes?.[0], // 始发站点
      originRegionCode: params?.originRegionCodes?.[0], // 始发区域编码
      destinationRegionCode: params?.destinationRegionCodes?.[0], // 目的区域编码
    });

    const { data = [] } = response || {};
    return data;
  } catch (error) {
    console.error('Error fetching fullRateStandard:', error);
    return [];
  }
}

/**
 * 获取里程信息
 *
 * @param params 请求参数
 * @returns 返回里程信息数组
 */
async function fetchMileage(params) {
  console.group('🔍 里程查询服务');
  console.log('📋 输入参数:', params);

  try {
    const requestParams = {};
    let originType = '未知';
    let destinationType = '未知';

    // 编码处理阶段
    console.group('🔧 编码处理');

    // 处理始发编码
    if (params?.scanSiteCodes?.[0]) {
      // 分拨编码（7位数）
      requestParams.departureSiteCode = params.scanSiteCodes[0];
      originType = `分拨(${params.scanSiteCodes[0]})`;
      console.log('📍 始发编码: 分拨', params.scanSiteCodes[0]);
    } else if (params?.originProvinceAreaCodes?.[0]) {
      // 营运区编码（8位数）
      requestParams.originProvinceAreaCode = params.originProvinceAreaCodes[0];
      originType = `营运区(${params.originProvinceAreaCodes[0]})`;
      console.log('📍 始发编码: 营运区', params.originProvinceAreaCodes[0]);
    } else if (params?.originRegionCodes?.[0]) {
      // 区域编码（5位数）
      requestParams.originRegionCode = params.originRegionCodes[0];
      originType = `区域(${params.originRegionCodes[0]})`;
      console.log('📍 始发编码: 区域', params.originRegionCodes[0]);
    } else {
      console.warn('⚠️ 未找到有效的始发编码');
    }

    // 处理目的编码
    if (params?.nextSiteCodes?.[0]) {
      // 分拨编码（7位数）
      requestParams.arriveSiteCode = params.nextSiteCodes[0];
      destinationType = `分拨(${params.nextSiteCodes[0]})`;
      console.log('🎯 目的编码: 分拨', params.nextSiteCodes[0]);
    } else if (params?.destinationProvinceAreaCodes?.[0]) {
      // 营运区编码（8位数）
      requestParams.destinationProvinceAreaCode = params.destinationProvinceAreaCodes[0];
      destinationType = `营运区(${params.destinationProvinceAreaCodes[0]})`;
      console.log('🎯 目的编码: 营运区', params.destinationProvinceAreaCodes[0]);
    } else if (params?.destinationRegionCodes?.[0]) {
      // 区域编码（5位数）
      requestParams.destinationRegionCode = params.destinationRegionCodes[0];
      destinationType = `区域(${params.destinationRegionCodes[0]})`;
      console.log('🎯 目的编码: 区域', params.destinationRegionCodes[0]);
    } else {
      console.warn('⚠️ 未找到有效的目的编码');
    }

    console.log('🚀 查询类型:', `${originType} → ${destinationType}`);
    console.groupEnd();

    // API请求阶段
    console.group('📡 API请求');
    console.log('📤 请求参数:', requestParams);
    console.log('🌐 请求地址: /api/route/queryMileage');

    const response = await crudRequest('/api/route/queryMileage', requestParams);
    const { data = [] } = response || {};
    console.groupEnd();

    // 结果处理阶段
    console.group('📊 响应结果');
    console.log('📥 数据量:', data.length);

    if (data.length > 0) {
      console.log('✅ 查询成功');
      console.table(
        data.slice(0, 3).map((item) => ({
          距离: item.distance,
          始发: item.departureSiteCode || item.originProvinceAreaCode || item.originRegionCode,
          目的:
            item.arriveSiteCode || item.destinationProvinceAreaCode || item.destinationRegionCode,
          车型: item.carType,
        })),
      );
    } else {
      console.warn('⚠️ 未查询到里程信息');
    }
    console.groupEnd();

    console.groupEnd(); // 结束主组
    return data;
  } catch (error) {
    console.groupEnd(); // 确保在错误时也关闭所有组
    console.group('❌ 错误信息');
    console.error('查询失败:', error.message || error);
    console.error('错误详情:', error);
    if (error.stack) {
      console.error('错误堆栈:', error.stack);
    }
    console.groupEnd();
    console.groupEnd(); // 结束主组
    return [];
  }
}

// 拆分接口
const requestAll = async (params) => {
  let _params = {
    scanSites: ['萧山分拨中心'],
    queryType: 30,
    startDate: '2023-03-08 12:00:00',
    endDate: '2023-03-15 12:00:00',
    carPlanDepartStartDate: '2023-03-08 12:00:00',
    carPlanDepartEndDate: '2024-03-15 12:00:00',
    areaPropertys: ['10', '20', '30', '40'],
  };
  // params = isDev ? _params : params;
  console.log(params);
  const allResult = await Promise.all([
    // crudRequest('/api/route/selectSrForecastBySite', params), // 接口拆分--网点预到达
    // crudRequest('/api/route/selectSrForecastByTrunkLine', params), // 接口拆分--干线预到达
    crudRequest('/api/route/selectSrForecastBySiteAndTrunkLine', params), // 接口拆分--网点预到达 干线预到达
    crudRequest('/api/route/selectSrForecastByRepertory', params), //  接口拆分--在库
    crudRequest('/api/route/selectSrForecastByLoadNoSend', params), // 接口拆分--已装未发
    crudRequest('/api/route/selectSrForecastByVolume', params), // 接口拆分--运力
  ]);

  let allLineDirect = {};
  allResult.forEach((result) => {
    (result?.data || []).forEach((item) => {
      if (allLineDirect[item?.site]) {
        allLineDirect[item?.site] = { ...allLineDirect[item?.site], ...item };
      } else {
        allLineDirect[item?.site] = item;
      }
    });
  });

  // 查询满载率标准
  const fullRateStandardData = await fetchFullRateStandard(params);
  // 查询流向排序
  const flowOrder = await fetchFlowOrder(params);
  // 查询里程
  const mileageData = await fetchMileage(params);
  let volumes = Object.values(allLineDirect);
  let newVolumes = volumes.map((item, index) => {
    // 深拷贝 item 确保不会改变原始数据
    let newItem = cloneDeep(item);
    newItem.oldItem = {
      ...item,
    };
    // 使用 getLoadRateStandard 方法获取满载标准
    newItem.loadRateStandardPercentage = getLoadRateStandard(
      fullRateStandardData,
      newItem.departureSiteCode, // 始发分拨的代码
      newItem.arrivalSiteCode, // 目的分拨的代码
    );
    newItem.distance = getDistance(
      mileageData,
      newItem.departureSiteCode, // 始发分拨的代码
      newItem.arrivalSiteCode, // 目的分拨的代码
    );

    if (
      params.distance &&
      ((params.distance === 10 && newItem.distance > 550) ||
        (params.distance === 20 && newItem.distance <= 550))
    ) {
      return;
    }
    // 确保所有参与计算的item属性都有默认值
    const originCalcWeight = parseFloat(newItem.originCalcWeight) || 0; // 网点预到达
    const trainsCalcWeight = parseFloat(newItem.trainsCalcWeight) || 0; // 中转预到达
    const unloadedCalcWeight = parseFloat(newItem.unloadedCalcWeight) || 0; // 在库
    const mergeCalcWeight = parseFloat(newItem.mergeCalcWeight) || 0; // 已装未发
    const arrNoUnloadedWeight = parseFloat(newItem?.arrNoUnloadedWeight) || 0; // 已到未卸
    const actualVolumesZbBig = parseFloat(newItem.actualVolumesZbBig) || 0;
    const actualVolumeJbBig = parseFloat(newItem.actualVolumeJbBig) || 0;
    const actualVolumesZb = parseFloat(newItem.actualVolumesZb) || 0; // 正班
    const actualVolumeJb = parseFloat(newItem.actualVolumeJb) || 0; // 加班
    const fullRateStandard = Number(newItem.fullRateStandard) || 1; // 流向的满载率标准

    const originGoodsVolume = parseFloat(newItem.originGoodsVolume) || 0; // 网点预到达体积
    const trainsGoodsVolume = parseFloat(newItem.trainsGoodsVolume) || 0; // 中转预到达体积
    const unloadedGoodsVolume = parseFloat(newItem.unloadedGoodsVolume) || 0; // 在库体积
    const mergeGoodsVolume = parseFloat(newItem.mergeGoodsVolume) || 0; // 已装未发体积

    // 计算setTotal，预出发 合计
    // setTotal = 网点预到达 + 中转预到达 + 在库 + 已装未发 + 已到未卸
    newItem.setTotal = parseFloat(
      (
        originCalcWeight +
        trainsCalcWeight +
        unloadedCalcWeight +
        mergeCalcWeight +
        arrNoUnloadedWeight
      ).toFixed(1),
    );
    // 留仓 = 在库 + 已装未发
    newItem.unloadedCalcWeightTotal = parseFloat((unloadedCalcWeight + mergeCalcWeight).toFixed(2));
    // 预出发 合计体积
    newItem.setTotalVolume = parseFloat(
      (originGoodsVolume + trainsGoodsVolume + unloadedGoodsVolume + mergeGoodsVolume).toFixed(1),
    );

    // 计算gapVolume， 运力缺口1
    // gapVolume = 正班大运力 + 加班大运力 - 预出发合计

    newItem.gapVolume = parseFloat(
      (actualVolumesZbBig + actualVolumeJbBig - newItem.setTotal).toFixed(1),
    );

    // if (newItem.fullRateStandard) {
    const setTotal = new BigNumber(newItem.setTotal || 0);
    const actualVolumesZbVal = new BigNumber(actualVolumesZb || 0);
    const actualVolumeJbVal = new BigNumber(actualVolumeJb || 0);
    const fullRateStandardVal = new BigNumber(fullRateStandard || 1);
    const actualVolumes = actualVolumesZbVal.plus(actualVolumeJbVal);

    // 计算缺口2，使用 BigNumber 进行精确计算
    newItem.gapVolume2 = actualVolumes
      .times(fullRateStandardVal) // 实际运力 * 满载率标准
      .minus(setTotal) // 减去预计出发货量
      .dividedBy(fullRateStandardVal) // 除以满载率标准
      .toFixed(2); // 保留 2 位小数
    // 预出发 合计体积
    newItem.setTotalVolume = parseFloat(
      (originGoodsVolume + trainsGoodsVolume + unloadedGoodsVolume + mergeGoodsVolume).toFixed(1),
    );
    const setTotalVolume = new BigNumber(newItem.setTotalVolume || 0);
    if (index === 0) {
      console.table({
        缺口: newItem.gapVolume,
        缺口2: newItem.gapVolume2,
        满载率标准: fullRateStandardVal.toString(),
        满载装载标准维护: newItem?.loadRateStandardPercentage.toString(),
        加班运力: actualVolumeJbVal.toString(),
        正班运力: actualVolumesZbVal.toString(),
        实际运力: actualVolumes.toString(),
        预计出发货量: setTotal.toString(),
        预计出发货量体积: setTotalVolume.toString(),
      });
    }
    // } else {
    //   newItem.gapVolume2 = parseFloat(
    //     (actualVolumesZbBig + actualVolumeJbBig - newItem.setTotal).toFixed(1), // 运力缺口2 = (正班运力 + 加班运力 - 预计出发货量)
    //   );
    // }

    return {
      ...newItem,
    };
  });

  const sortedArray = sortLargeArrayBySmallArray(newVolumes, flowOrder);
  console.log(
    '%c [ newVolumes ]-249',
    'font-size:13px; background:pink; color:#bf2c9f;',
    newVolumes,
  );
  console.log(
    '%c [ sortedArray ]-250',
    'font-size:13px; background:pink; color:#bf2c9f;',
    sortedArray,
  );
  console.log('%c [ flowOrder ]-251', 'font-size:13px; background:pink; color:#bf2c9f;', flowOrder);
  return sortedArray;
};

export default {
  namespace: 'centerWeightViewTest2',
  state: {
    ...paramsForm,
    defualtDateFormat: 'YYYY-MM-DD HH:mm:00',
    onSearch: false,
    tableOffsetHeight: 0,
    datadbName: 'druid',

    dayAggChange: true, // true为按天统计表

    actualDepartureCarsItem: [], // 已发出 tms
    planDepartureCarsItem: [], // 计划发
    volumeItem: [], // 运力
    actualVolumeList: [], // 实际运力
    collectDataSource: [],
    detailDataSource: [],
    activeKey: '2',

    tableItem: [],
    page: 1,
    totalRow: {},
  },
  effects: {
    *getTms({}, { put, select }) {
      const { activeKey } = yield select((state) => state.centerWeightViewTest2);
      // tms
      yield put({ type: 'update', payload: { isTmsLoading: false } });
      let query = yield select(({ centerWeightViewTest2 }) => {
        return centerWeightViewTest2;
      });
      console.log('%c [ query ]-284', 'font-size:13px; background:pink; color:#bf2c9f;', query);

      // -- 营运区-营运区 10 分拨-营运区 20，分拨 - 分拨 30
      let queryType = 10;

      const params = {
        scanSiteCodes: handleParme(query?.collcetSearchParams?.departCenter, 'code'), // -- 出发分拨
        originProvinceAreas: handleParme(query?.collcetSearchParams?.departProvinceArea, 'name'), // -- 出发省区
        originProvinceAreaCodes: handleParme(
          query?.collcetSearchParams?.departProvinceArea,
          'code',
        ), // -- 出发省区编码
        nextSiteCodes: handleParme(query?.collcetSearchParams?.arrivalCenter, 'code'), // -- 下一分拨
        destinationProvinceAreas: handleParme(
          query?.collcetSearchParams?.arrivalProvinceArea,
          'name',
        ), // -- 下一省区
        destinationProvinceAreaCodes: handleParme(
          query?.collcetSearchParams?.arrivalProvinceArea,
          'code',
        ), // -- 下一省区编码 arrivalProvinceArea
        queryType: queryType, // -- 营运区-营运区 10 分拨-营运区 20，分拨- 分拨 30
        startDate: moment(query?.dateRange?.[0])?.format(query?.defualtDateFormat), // -- 运单开始时间
        endDate: moment(query?.dateRange?.[1])?.format(query?.defualtDateFormat), // -- 运单结束时间
        carPlanDepartStartDate: query?.carDateRange?.[0]
          ? moment(query?.carDateRange?.[0])?.format(query?.defualtDateFormat)
          : undefined, // -- 运单开始时间
        carPlanDepartEndDate: query?.carDateRange?.[1]
          ? moment(query?.carDateRange?.[1])?.format(query?.defualtDateFormat)
          : undefined, // -- 运单结束时间
        // 里程段
        distance: query?.detailSearchParams?.distance,
        areaPropertys: query.collcetSearchParams.areaPropertys ?? ['10', '20', '30', '40'],
        // 出发区域
        originRegions: handleParme2(query?.detailSearchParams?.departCenter, 'name'),
        // 出发区域编码
        originRegionCodes: handleParme2(query?.detailSearchParams?.departCenter, 'code'),
        // 到达区域
        destinationRegions: handleParme2(query?.detailSearchParams?.arrivalCenter, 'name'),
        // 到达区域编码
        destinationRegionCodes: handleParme2(query?.detailSearchParams?.arrivalCenter, 'code'),
      };
      if (activeKey === '1') {
        delete params['areaPropertys'];
      } else {
        params['areaPropertys'] = query.collcetSearchParams.areaPropertys ?? [
          '10',
          '20',
          '30',
          '40',
        ];
      }
      let volumes = yield requestAll(params);

      if (volumes?.length) {
        yield put({
          type: 'update',
          payload: {
            collectDataSource: volumes.map((item) => {
              item.children = [];
              return item;
            }),
          },
        });
      }
    },

    *jumpCenterToCenter({ payload }, { put }) {
      yield put({
        type: 'update',
        payload: {
          detailSearchParams: {
            // 出发分拨
            departCenter: { name: payload?.departureSite, code: payload?.departureSiteCode },
            // 到达营业区
            arrivalProvinceArea: { name: payload?.site?.split('-')?.[1] },
          },
        },
      });
      yield put({ type: 'getCenterToCenter' });
    },

    *getCenterToCenter({}, { put, select }) {
      const { activeKey } = yield select((state) => state.centerWeightViewTest2);
      // tms
      yield put({ type: 'update', payload: { isTmsLoading: false } });
      let query = yield select(({ centerWeightViewTest2 }) => {
        return centerWeightViewTest2;
      });
      console.log('%c [ query ]-358', 'font-size:13px; background:pink; color:#bf2c9f;', query);

      // -- 营运区-营运区 10 分拨-营运区 20，分拨 - 分拨 30
      let queryType = 30;
      const params = {
        scanSiteCodes: handleParme(query?.detailSearchParams?.departCenter, 'code'), // -- 出发分拨
        originProvinceAreas: handleParme(query?.detailSearchParams?.departProvinceArea, 'name'), // -- 出发省区
        originProvinceAreaCodes: handleParme(query?.detailSearchParams?.departProvinceArea, 'code'), // -- 出发省区编码
        nextSiteCodes: handleParme(query?.detailSearchParams?.arrivalCenter, 'code'), // -- 下一分拨
        destinationProvinceAreas: handleParme(
          query?.detailSearchParams?.arrivalProvinceArea,
          'name',
        ), // -- 下一省区
        destinationProvinceAreaCodes: handleParme(
          query?.detailSearchParams?.arrivalProvinceArea,
          'code',
        ), // -- 下一省区编码 arrivalProvinceArea
        queryType: queryType, // -- 营运区-营运区 10 分拨-营运区 20，分拨- 分拨 30
        startDate: moment(query?.dateRange?.[0])?.format(query?.defualtDateFormat), // -- 运单开始时间
        endDate: moment(query?.dateRange?.[1])?.format(query?.defualtDateFormat), // -- 运单结束时间
        carPlanDepartStartDate: query?.carDateRange?.[0]
          ? moment(query?.carDateRange?.[0])?.format(query?.defualtDateFormat)
          : undefined, // -- 运单开始时间
        carPlanDepartEndDate: query?.carDateRange?.[1]
          ? moment(query?.carDateRange?.[1])?.format(query?.defualtDateFormat)
          : undefined, // -- 运单结束时间
        // 出发区域
        originRegions: handleParme2(query?.detailSearchParams?.departCenter, 'name'),
        // 出发区域编码
        originRegionCodes: handleParme2(query?.detailSearchParams?.departCenter, 'code'),
        // 到达区域
        destinationRegions: handleParme2(query?.detailSearchParams?.arrivalCenter, 'name'),
        // 到达区域编码
        destinationRegionCodes: handleParme2(query?.detailSearchParams?.arrivalCenter, 'code'),
        // 里程段
        distance: query?.detailSearchParams?.distance,
        areaPropertys: query.detailSearchParams.areaPropertys ?? ['10', '20', '30', '40'],
      };
      if (activeKey === '1') {
        delete params['areaPropertys'];
      } else {
        params['areaPropertys'] = query.collcetSearchParams.areaPropertys ??
          query.detailSearchParams.areaPropertys ?? ['10', '20', '30', '40'];
      }
      let volumes = yield requestAll(params);
      console.log('%c [ volumes ]-415', 'font-size:13px; background:pink; color:#bf2c9f;', volumes);
      const volumes2 = cloneDeep(volumes);
      // 将流向名称一样的进行排序
      const sortDepartureSite = volumes2.sort((a, b) => {
        // 首先按照出发分拨中心排序
        const departureCompare = a.departureSite.localeCompare(b.departureSite);
        if (departureCompare !== 0) {
          return departureCompare;
        }
        // 如果出发分拨中心相同，则按照到达分拨中心排序
        return a.arrivalSite.localeCompare(b.arrivalSite);
      });
      console.log(
        '%c [ sortDepartureSite ]-417',
        'font-size:13px; background:pink; color:#bf2c9f;',
        sortDepartureSite,
      );
      if (volumes?.length) {
        yield put({
          type: 'update',
          payload: {
            queryInfo: {
              params,
            },
            detailDataSource: volumes,
          },
        });
      }
    },

    // 请求分拨到省区数据
    *getCenterToProvince({ payload }, { put, select }) {
      const { activeKey } = yield select((state) => state.centerWeightViewTest2);
      // tms
      yield put({ type: 'update', payload: { isTmsLoading: false } });
      let query = yield select(({ centerWeightViewTest2 }) => {
        return centerWeightViewTest2;
      });
      console.log('%c [ query ]-410', 'font-size:13px; background:pink; color:#bf2c9f;', query);

      // -- 营运区-营运区 10 分拨-营运区 20，分拨 - 分拨 30
      let queryType = 20;

      // const start = moment(query.startTime).day();
      const params = {
        // scanSites: [payload?.site?.split('-')?.[0]] || undefined, // -- 出发分拨
        originProvinceAreas: [payload?.site?.split('-')?.[0]] || undefined, // -- 出发省区
        // originProvinceAreaCodes: handleParme(
        //   query?.collcetSearchParams?.departProvinceArea,
        //   'code',
        // ), // -- 出发省区编码
        // nextSites: handleParme(query?.collcetSearchParams?.arrivalCenter, 'name'), // -- 下一分拨
        destinationProvinceAreas: [payload?.site?.split('-')?.[1]] || undefined, // -- 下一省区
        // destinationProvinceAreaCodes: handleParme(query?.collcetSearchParams?.arrivalProvinceArea, 'code'), // -- 下一省区编码 arrivalProvinceArea
        queryType: queryType, // -- 营运区-营运区 10 分拨-营运区 20，分拨- 分拨 30
        startDate: moment(query?.dateRange?.[0])?.format(query?.defualtDateFormat), // -- 运单开始时间
        endDate: moment(query?.dateRange?.[1])?.format(query?.defualtDateFormat), // -- 运单结束时间
        carPlanDepartStartDate: query?.carDateRange?.[0]
          ? moment(query?.carDateRange?.[0])?.format(query?.defualtDateFormat)
          : undefined, // -- 运单开始时间
        carPlanDepartEndDate: query?.carDateRange?.[1]
          ? moment(query?.carDateRange?.[1])?.format(query?.defualtDateFormat)
          : undefined, // -- 运单结束时间
        // 出发区域
        originRegions: handleParme2(query?.detailSearchParams?.departCenter, 'name'),
        // 出发区域编码
        originRegionCodes: handleParme2(query?.detailSearchParams?.departCenter, 'code'),
        // 到达区域
        destinationRegions: handleParme2(query?.detailSearchParams?.arrivalCenter, 'name'),
        // 到达区域编码
        destinationRegionCodes: handleParme2(query?.detailSearchParams?.arrivalCenter, 'code'),
        areaPropertys: query.collcetSearchParams.areaPropertys ?? ['10', '20', '30', '40'],
      };

      if (activeKey === '1') {
        delete params['areaPropertys'];
      } else {
        params['areaPropertys'] = query.collcetSearchParams.areaPropertys ?? [
          '10',
          '20',
          '30',
          '40',
        ];
      }
      let volumes = yield requestAll(params);
      if (volumes?.length) {
        payload.children = volumes;
        // payload.children = query.collectDataSource;
        let newDataSource = query.collectDataSource.map((item) => {
          // 先拷贝再改属性，避免污染 state 中的原对象
          const next = { ...item };
          if (next.site === payload.site) {
            next.children = volumes;
          }
          if (!next.children) {
            next.children = [];
          }
          return next;
        });
        yield put({
          type: 'update',
          payload: {
            collectDataSource: [...newDataSource],
          },
        });
      }
    },

    // *handleSum({ payload }, { call, put, select }) {
    //   const totalRow = {
    //     totalRow: '合计',
    //     sum0: 0,
    //     sum1: 0,
    //     sum2: 0,
    //     sum3: 0,
    //     sum4: 0,
    //     sum5: 0,
    //     sum6: 0,
    //     sum7: 0,
    //     sum8: 0,
    //   };
    //   allSite.forEach((item, index) => {
    //     totalRow.sum0 += item.originCalcWeight; // 网点预到达
    //     totalRow.sum1 += item.trainsCalcWeight; // 中转预到达
    //     totalRow.sum2 += item.unloadedCalcWeight; // 库存
    //     totalRow.sum3 += item.mergeCalcWeight; // 已装未发
    //     totalRow.sum4 += item.setTotal; // 预出发合计
    //     totalRow.sum5 += item.volumes; // 规划运力
    //     totalRow.sum6 += item.actualVolumes0; // 实际运力-正班车
    //     totalRow.sum7 += item.actualVolumes1; // 实际运力-加班车
    //     totalRow.sum8 += item.gapVolume; // 运力缺口
    //   });
    //   yield put({
    //     type: 'update',
    //     payload: {
    //       tableItem: allSite,
    //       totalRow,
    //     },
    //   });
    // },

    *queryAuthority({}, { call, put, select }) {
      const apiPath = '/api/route/queryAuthority';
      const response = yield call(crudRequest, apiPath, {});

      let query = yield select(({ centerWeightViewTest2 }) => {
        return centerWeightViewTest2;
      });

      if (response.code === 1 && response.data) {
        if (response?.data?.[0]?.siteName) {
          yield put({
            type: 'update',
            payload: {
              collcetSearchParams: {
                ...query.collcetSearchParams,
                departProvinceArea: { name: response?.data?.[0]?.siteName },
              },
            },
          });
        }
      }
    },
    *queryForecastByAllLoadNoSend({ callback }, { select }) {
      let query = yield select(({ centerWeightViewTest2 }) => {
        return centerWeightViewTest2;
      });
      const datas = query.detailDataSource || [];
      const sites = datas.map((item) => item.site);
      let taskNosList = [];
      datas.forEach((item) => {
        if (item.taskNos && item.taskNos.length > 0) {
          item.taskNos.forEach((n) => taskNosList.push(n));
        }
      });

      const params = {
        originProvinceAreas: handleParme(query?.detailSearchParams?.departProvinceArea, 'name'), // -- 出发省区
        originProvinceAreaCodes: handleParme(query?.detailSearchParams?.departProvinceArea, 'code'), // -- 出发省区编码
        scanSiteCodes: handleParme(query?.detailSearchParams?.departCenter, 'code'), // -- 出发分拨
        nextSiteCodes: handleParme(query?.detailSearchParams?.arrivalCenter, 'code'), // -- 下一分拨
        queryType: 30, // -- 营运区-营运区 10 分拨-营运区 20，分拨- 分拨 30
        startDate: moment(query?.dateRange?.[0])?.format(query?.defualtDateFormat), // -- 运单开始时间
        endDate: moment(query?.dateRange?.[1])?.format(query?.defualtDateFormat), // -- 运单结束时间
        carPlanDepartStartDate: query?.carDateRange?.[0]
          ? moment(query?.carDateRange?.[0])?.format(query?.defualtDateFormat)
          : undefined, // -- 运单开始时间
        carPlanDepartEndDate: query?.carDateRange?.[1]
          ? moment(query?.carDateRange?.[1])?.format(query?.defualtDateFormat)
          : undefined, // -- 运单结束时间
        // 出发区域
        originRegions: handleParme2(query?.detailSearchParams?.departCenter, 'name'),
        // 出发区域编码
        originRegionCodes: handleParme2(query?.detailSearchParams?.departCenter, 'code'),
        // 到达区域
        destinationRegions: handleParme2(query?.detailSearchParams?.arrivalCenter, 'name'),
        // 到达区域编码
        destinationRegionCodes: handleParme2(query?.detailSearchParams?.arrivalCenter, 'code'),
        taskNos: taskNosList,
        sites,
      };
      const { code, data } = yield crudRequest(
        '/api/route/selectSrForecastByAllLoadNoSend',
        params,
      );
      if (code === 1) {
        callback?.(data);
      }
    },
    /**
     * 导出明细处理函数
     * 根据不同的类型导出相应的库存或装卸车货量
     *
     * @param {Object} payload - 参数对象
     * @param {string} payload.modalsTitle - 导出标题，决定导出类型
     * @param {Function} payload.callback - 导出成功后的回调函数
     * @param {Object} effects - Redux-Saga effects对象
     */

    *inStockExport({ payload, callback }, { select }) {
      const titleUrlMap = {
        库存货量: '/api/route/inStockSrExport/1',
        卸车货量: '/api/route/inStockSrExport/2',
        装车货量: '/api/route/inStockSrExport/3',
        已装未发: '/api/route/loadedUnbilledSrExport',
        网点预到达: '/api/route/branchCargoQuantitySrExport',
        中转预到达: '/api/route/transitShipmentSrExport',
      };

      const { modalsTitle } = payload;
      console.log(
        '%c [ modalsTitle ]-399',
        'font-size:13px; background:pink; color:#bf2c9f;',
        modalsTitle,
      );
      let url = titleUrlMap[modalsTitle];
      console.log('%c [ url ]-401', 'font-size:13px; background:pink; color:#bf2c9f;', url);

      if (!url) {
        console.error(`Invalid modalsTitle: ${modalsTitle}`);
        return; // 或者你可以抛出错误 throw new Error(`Invalid modalsTitle: ${modalsTitle}`);
      }

      yield crudRequest(url);
      callback?.();
    },
    *queryForecastByAllInBound({ callback }, { select, put }) {
      let query = yield select(({ centerWeightViewTest2 }) => {
        return centerWeightViewTest2;
      });
      const datas = query.detailDataSource || [];
      const sites = datas.map((item) => item.site);
      let taskNosList = [];
      datas.forEach((item) => {
        if (item.taskNos && item.taskNos.length > 0) {
          item.taskNos.forEach((n) => taskNosList.push(n));
        }
      });
      const condit = {
        originProvinceAreas: handleParme(query?.detailSearchParams?.departProvinceArea, 'name'), // -- 出发省区
        originProvinceAreaCodes: handleParme(query?.detailSearchParams?.departProvinceArea, 'code'), // -- 出发省区编码
        scanSiteCodes: handleParme(query?.detailSearchParams?.departCenter, 'code'), // -- 出发分拨
        nextSiteCodes: handleParme(query?.detailSearchParams?.arrivalCenter, 'code'), // -- 下一分拨
        queryType: 30, // -- 营运区-营运区 10 分拨-营运区 20，分拨- 分拨 30
        startDate: moment(query?.dateRange?.[0])?.format(query?.defualtDateFormat), // -- 运单开始时间
        endDate: moment(query?.dateRange?.[1])?.format(query?.defualtDateFormat), // -- 运单结束时间
        carPlanDepartStartDate: query?.carDateRange?.[0]
          ? moment(query?.carDateRange?.[0])?.format(query?.defualtDateFormat)
          : undefined, // -- 运单开始时间
        carPlanDepartEndDate: query?.carDateRange?.[1]
          ? moment(query?.carDateRange?.[1])?.format(query?.defualtDateFormat)
          : undefined, // -- 运单结束时间
        // 出发区域
        originRegions: handleParme2(query?.detailSearchParams?.departCenter, 'name'),
        // 出发区域编码
        originRegionCodes: handleParme2(query?.detailSearchParams?.departCenter, 'code'),
        // 到达区域
        destinationRegions: handleParme2(query?.detailSearchParams?.arrivalCenter, 'name'),
        // 到达区域编码
        destinationRegionCodes: handleParme2(query?.detailSearchParams?.arrivalCenter, 'code'),
        destinationProvinceAreas: handleParme(
          query?.detailSearchParams?.arrivalProvinceArea,
          'name',
        ), // -- 下一省区
        destinationProvinceAreaCodes: handleParme(
          query?.detailSearchParams?.arrivalProvinceArea,
          'code',
        ), // -- 下一省区编码 arrivalProvinceArea
        taskNos: taskNosList,
        sites,
      };
      const params = {
        taskParams: JSON.stringify(condit),
        taskName: '在库明细导出',
        taskType: 'waybill_inbound_export',
        isAllSite: 0,
        taskStatus: 1,
      };
      const { code, data, message } = yield crudRequest(
        '/api/route/exportTask/createTask',
        params,
        { module: 'basic' },
      );
      // const { code, data } = yield crudRequest('/api/route/selectSrForecastByAllInBound', params);
      if (code === 1) {
        // yield put({ type: 'filedownload/updateState', payload: { messageInfo: message } });
        messagePop.success('请到导出页面下载');
        // callback?.(data);
      } else {
        messagePop.success(message ? message : '导出失败');
      }
    },
  },
  reducers: {
    update(state, { payload }) {
      return { ...state, ...payload };
    },
    updateSearchParams(state, { payload }) {
      return { ...state, searchParams: { ...state.searchParams, ...payload } };
    },
  },
};

// 排除文件 CenterArrivalBoard,mock,CenterDepartBoard,ForecastCenterDepartBoard,centerMassageMap
